import { Box, Typography } from "@mui/material";

/**
 * CandidateFunnel — Phase 6 (Design Gap Audit item F.2) reusable component.
 *
 * Horizontal stage-by-stage bar funnel, matching the prototype's candidate
 * funnel visualization.
 *
 * KNOWN DATA LIMITATION (reported honestly, not hidden): the existing
 * `Candidate.feedback` field stores only the candidate's CURRENT stage, not
 * a history of every stage they ever passed through. So a candidate who
 * reached L2_ROUND and was later REJECTED shows up only in the "Rejected"
 * bucket today — they are NOT counted in the Shortlisted/Interviewed
 * buckets they actually passed through earlier. This means the funnel
 * below is a snapshot of current distribution, not a true historical
 * cumulative funnel — it will under-count earlier stages compared to a
 * system that tracks per-stage transition history. This is a real
 * constraint of the existing data model, not something fixable from the
 * frontend, and is called out explicitly in the implementation report
 * rather than presented as more precise than it is.
 *
 * Props:
 *   stages - array of { label, value }, in funnel order (required).
 *            `value` must come from real counts the caller computed from
 *            already-loaded candidate data — never fabricated.
 *   color  - bar color (default brand blue)
 */
export default function CandidateFunnel({ stages, color = "#2563eb" }) {
  const max = Math.max(...stages.map((s) => s.value), 1);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {stages.map((s, i) => {
        const pct = Math.round((s.value / max) * 100);
        const prevValue = i > 0 ? stages[i - 1].value : null;
        const conversion = prevValue
          ? Math.round((s.value / prevValue) * 100)
          : null;
        return (
          <Box key={s.label}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}
            >
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                {s.value}
                {conversion !== null && ` · ${conversion}% of prev.`}
              </Typography>
            </Box>
            <Box
              sx={{
                height: 10,
                borderRadius: "6px",
                bgcolor: "action.hover",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${pct}%`,
                  bgcolor: color,
                  borderRadius: "6px",
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
